import { ImageLoader } from 'next/image';
import { IMAGEKIT_DEFAULT_TRANSFORM } from 'src/constants/constants';

export type CarmaImageFit = 'cover' | 'fit';

type CarmaImageLoaderProps = {
  src: string;
  width: number;
  height?: number;
  quality?: number;
  fit?: CarmaImageFit;
};

type CarmaImageLoader = (props: CarmaImageLoaderProps) => string;

const DEFAULT_IMAGE_QUALITY = 70;

/**
 * Encode an image URL
 *
 * For example encode the spaces in the URL with %20
 * @param src
 */
export const encodeImageSrc = (src: string): string => {
  return src.replaceAll(' ', '%20');
};

export const imageLoaderCarma: CarmaImageLoader = ({ src, width, height, quality, fit }) => {
  const processedSrc = src.replaceAll(IMAGEKIT_DEFAULT_TRANSFORM, '');
  // No format optimisation for SVG
  if (src.endsWith('.svg')) {
    return processedSrc;
  }

  const params = ['f-auto', `w-${width}`, `q-${quality ?? DEFAULT_IMAGE_QUALITY}`];

  if (height) {
    params.push(`h-${height}`);

    // Only set the fit if height is defined
    if (fit === 'fit') {
      params.push('c-maintain_ratio');
    }
  }

  return `${processedSrc}?tr=${params.join(',')}`;
};

const imageLoaderContentful: CarmaImageLoader = ({ src, width, height, quality, fit }) => {
  // No format optimisation for SVG
  if (src.endsWith('.svg')) {
    return src;
  }

  const params = new URLSearchParams({
    w: `${width}`,
    q: `${quality ?? DEFAULT_IMAGE_QUALITY}`,
    fm: 'webp',
  });

  if (height) {
    params.set('h', `${height}`);

    // Only set the fit if height is defined
    if (fit === 'fit') {
      params.set('fit', 'pad');
    } else {
      params.set('fit', 'fill');
    }
  }

  return `${src}?${params.toString()}`;
};

const defaultLoader: CarmaImageLoader = ({ src }) => src;

const imageLoaders: { [key: string]: CarmaImageLoader } = {
  'img.carma.com.au': imageLoaderCarma,
  'images.ctfassets.net': imageLoaderContentful,
};

const imageLoadersKeys = Object.keys(imageLoaders);

export const hasImageLoader = (src: string = '') => {
  if (typeof src === 'string') {
    const loaderKey = imageLoadersKeys.find((item) => src.includes(item));
    return !!(loaderKey && loaderKey in imageLoaders);
  }
  return false;
};

export const getCarmaGenericImageLoader = (src: string = '') => {
  if (typeof src === 'string') {
    const loaderKey = imageLoadersKeys.find((item) => src.includes(item));
    if (loaderKey && loaderKey in imageLoaders) {
      return imageLoaders[loaderKey];
    }
  }
  console.warn(`No image loader found for src: "${src}"`);
  return defaultLoader;
};

/**
 * This function is intended to be used as image loader within the NextJS Image component
 * In case the image src doesn't have a dedicated loader,
 * returns `undefined` to allow NextJS own image optimisation
 */
const carmaGenericImageLoader: ImageLoader = ({ src, width, quality }) => {
  // End early if invalid data passed
  if (typeof src !== 'string') {
    return '';
  }

  // Encode spaces in the src URL
  const encodedSrc = encodeImageSrc(src);

  // check if the src is part of the supported loaders
  const loader = getCarmaGenericImageLoader(encodedSrc);

  return loader({ src: encodedSrc, width, quality });
};

// Default export required for use in next.config.js
export default carmaGenericImageLoader;
